// A wallet or transaction lookup carried inside the page itself.
//
// Google indexed these pages as "Could not reach the block explorer". Its
// renderer runs the app, but the one request the page depends on - to
// blockchain.info - fails there, logged in Search Console only as "Other
// error", while every other cross-origin request on the page loads. So the
// balance and history that are the whole point of the page never reached the
// index, for any of the tens of thousands of wallets it holds.
//
// The fix is to not need that request. The edge function makes it instead and
// writes the answer into the HTML, and the page reads it from there. Anything
// that runs the app - a person, Google - then renders from data it already has.
//
// Shared by both halves so they cannot disagree: the edge function asks the
// same URL the browser would, and the element it writes is the one the page
// looks for. Imports nothing, for the same reason btcId.js does not - the
// browser, the edge function and the tests all load it.

const ELEMENT_ID = "lookup-data";

// The one place these URLs are spelled out. The browser falls back to them
// when nothing was embedded, so both paths render the same response.
export const lookupUrl = (kind, id) =>
  kind === "transaction"
    ? `https://blockchain.info/rawtx/${encodeURIComponent(id)}`
    : `https://blockchain.info/multiaddr?active=${encodeURIComponent(id)}`;

// The element the edge function appends to the page, or "" for an id that
// could not have come from verifyId - it is only ever an address or a hash, so
// anything else is refused rather than escaped into an attribute.
//
// Inside a script element, only "</script" can end the data early, so every "<"
// is written as its JSON escape; ">" and "&" go too, which costs nothing and
// leaves no markup in the payload at all. The result is still valid JSON.
export const lookupTag = (id, data) => {
  if (!/^[A-Za-z0-9]+$/.test(id)) return "";
  const json = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
  return `<script type="application/json" id="${ELEMENT_ID}" data-id="${id}">${json}</script>`;
};

// The embedded lookup for this id, or null to fetch it as before.
//
// Removed once read. The page is a single-page app, so the same document
// serves every lookup the visitor goes on to make; data left behind would be
// the served wallet's, and the id check alone would still hand a later visit
// back to that id a copy from whenever the tab was opened.
export const takeEmbeddedLookup = (id) => {
  const element = document.getElementById(ELEMENT_ID);
  if (!element || element.dataset.id !== id) return null;
  element.remove();
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    console.error("Embedded lookup unreadable, fetching instead:", error);
    return null;
  }
};
